import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Input, showNotification } from 'sonata-react-components';

import { generateNewMediaAdvertiser } from '@/services/mediaInfo.service';
import { MediaInfo, SelectedMediaInfo } from '../types';
import { NewAdvertiserInputStyled } from './newAdvertiserInput.style';

type Props = {
  isDisabled?: boolean;
  mediaInfoDictionary: MediaInfo[];
  mediaInfoSelected: SelectedMediaInfo;
  onClose: () => void;
  onMediaAdvertiserChange: (mediaAdvertiserId: string | null) => void;
  onMediaInfoSelectedChange: (mediaInfo: SelectedMediaInfo) => void;
  updateMediaInfoDictionaries: (mediaInfo: MediaInfo) => void;
};
type State = {
  isNewAdvertiserBeenCreated: boolean;
  newAdvertiserName: string;
  newAdvertiserUrl: string;
};

const NewAdvertiserInput: React.FC<Props> = ({
  isDisabled = false,
  mediaInfoDictionary,
  mediaInfoSelected,
  onClose,
  onMediaAdvertiserChange,
  onMediaInfoSelectedChange,
  updateMediaInfoDictionaries
}) => {
  const { formatMessage } = useIntl();

  const [{ isNewAdvertiserBeenCreated, newAdvertiserName, newAdvertiserUrl }, setState] = useState<State>({
    isNewAdvertiserBeenCreated: false,
    newAdvertiserName: '',
    newAdvertiserUrl: ''
  });

  const onChangeNewAdvertiserName = (name: string | FileList | null) => {
    setState(prev => ({
      ...prev,
      newAdvertiserName: name as string
    }));
  };

  const onChangeNewAdvertiserUrl = (url: string | FileList | null) => {
    setState(prev => ({
      ...prev,
      newAdvertiserUrl: url as string
    }));
  };

  const createNewMediaAdvertiser = async () => {
    const { agency, group } = mediaInfoSelected;
    setState(prev => ({
      ...prev,
      isNewAdvertiserBeenCreated: true
    }));
    try {
      const newAdvertiser = await generateNewMediaAdvertiser({
        mediaAgency: agency._id || undefined,
        mediaGroup: group._id || undefined,
        name: newAdvertiserName,
        url: newAdvertiserUrl
      });
      showNotification(
        'notify',
        formatMessage({
          defaultMessage: 'created',
          id: 'advertiserCampaignSelector.newAdvertiser.created'
        })
      );
      onMediaInfoSelectedChange({
        advertiser: newAdvertiser,
        agency,
        group
      });
      onMediaAdvertiserChange(newAdvertiser._id);
      updateMediaInfoDictionaries(newAdvertiser);
      onClose();
    } catch (_e: any) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'There was a problem saving your advertiser. Please try again later',
          id: 'advertiserCampaignSelector.newAdvertiser.error'
        })
      );
      onMediaInfoSelectedChange({
        advertiser: agency.advertisers.find(mAdv => mAdv._id === null)!,
        agency,
        group
      });
      onMediaAdvertiserChange(null);
      setState(prev => ({
        ...prev,
        isNewAdvertiserBeenCreated: false
      }));
    }
  };

  const checkNewAdvertiser = (advertiserName: string, advertiserUrl: string) => {
    if (advertiserName === '' || advertiserUrl === null) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Please fill advertiser name to create new one',
          id: 'advertiserCampaignSelector.newAdvertiser.validName'
        })
      );
      return false;
    }
    if (advertiserUrl === '' || !taptap.tools.validateUrl(advertiserUrl)) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Please fill a valid advertiser URL to create new one',
          id: 'advertiserCampaignSelector.newAdvertiser.validUrl'
        })
      );
      return false;
    }
    const selectedGroup = mediaInfoDictionary.find(mGroup => mediaInfoSelected.group._id === mGroup._id)!;
    const selectedAgency = selectedGroup.agencies.find(mAgency => mediaInfoSelected.agency._id === mAgency._id)!;
    const existingAdvertiser = selectedAgency.advertisers.find(mAdv => {
      return advertiserName.toLowerCase() === mAdv.name.toLowerCase();
    });
    if (existingAdvertiser) {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage: 'This advertiser already exists',
          id: 'advertiserCampaignSelector.newAdvertiser.exists'
        })
      );
      onMediaInfoSelectedChange({
        advertiser: existingAdvertiser,
        agency: selectedAgency,
        group: selectedGroup
      });
      onMediaAdvertiserChange(existingAdvertiser._id);
      return false;
    }
    return true;
  };

  const onSaveNewAdvertiser = () => {
    if (checkNewAdvertiser(newAdvertiserName, newAdvertiserUrl)) {
      createNewMediaAdvertiser();
    }
  };

  const onCancelNewAdvertiser = () => {
    onMediaInfoSelectedChange({
      advertiser: mediaInfoSelected.agency.advertisers.find(mAdv => mAdv._id === null)!,
      agency: mediaInfoSelected.agency,
      group: mediaInfoSelected.group
    });
    onMediaAdvertiserChange(null);
    onClose();
  };

  return (
    <NewAdvertiserInputStyled className="newAdvertiserInput">
      <div className="newAdvertiserInput__advertiserNameInputContainer">
        <Input
          className="newAdvertiserInput__advertiserNameInput"
          disabled={isDisabled}
          id="advertiserNameInput"
          label={formatMessage({
            defaultMessage: 'New Advertiser Name',
            id: 'advertiserCampaignSelector.newAdvertiser.newAdvertiserName'
          })}
          name="advertiserNameInput"
          notFloatingPlaceholder
          onChange={onChangeNewAdvertiserName}
          placeholder={formatMessage({
            defaultMessage: 'Advertiser Name',
            id: 'advertiserCampaignSelector.newAdvertiser.advertiserName'
          })}
          required
          shape="square"
          type="text"
        />
      </div>
      <div className="newAdvertiserInput__advertiserUrlInputContainer">
        <Input
          className="newAdvertiserInput__advertiserUrlInput"
          disabled={isDisabled}
          id="advertiserUrlInput"
          label={formatMessage({
            defaultMessage: 'New Advertiser URL',
            id: 'advertiserCampaignSelector.newAdvertiser.newAdvertiserUrl'
          })}
          name="advertiserUrlInput"
          notFloatingPlaceholder
          onChange={onChangeNewAdvertiserUrl}
          placeholder={formatMessage({
            defaultMessage: 'Advertiser URL',
            id: 'advertiserCampaignSelector.newAdvertiser.advertiserUrl'
          })}
          required
          shape="square"
          type="text"
        />
        <div className="newAdvertiserInput__buttonsContainer">
          <Button
            className="newAdvertiserInput__buttonSave"
            disabled={isNewAdvertiserBeenCreated || isDisabled}
            icon="fa fa-check"
            onClick={onSaveNewAdvertiser}
            shape="round"
            size="small"
            type="primary"
          />
          <Button
            className="newAdvertiserInput__buttonCancel"
            disabled={isNewAdvertiserBeenCreated || isDisabled}
            icon="fa fa-times"
            onClick={onCancelNewAdvertiser}
            shape="round"
            size="small"
            type="primary"
          />
        </div>
      </div>
    </NewAdvertiserInputStyled>
  );
};

export default NewAdvertiserInput;
